import { Driver, Org, User } from '@hero/schema';

/**
 * Header note (auth-v2):
 *
 * The legacy `*_USER_TYPE` constants have been removed. The unified
 * `signUpSchema` and `signInSchema` discriminate by the wizard's
 * `personType` + `PartyRole[]` — see
 * `docs/relations-v2/08-user-identity.md` and the auth-v2 plan.
 *
 * The `carrierSignupSchema` / `customerSignupSchema` legacy schemas
 * still exist for the deprecation window but use inline string enum
 * literals instead of pulling from `@hero/core`'s deleted registry.
 */

/** @deprecated legacy `User.type` constant kept for schema literals. */
//* carrier types
export const freelancer_driver = 'DRIVER_FREELANCER_USER_TYPE' as const;
/** @deprecated legacy `User.type` constant kept for schema literals. */
export const carrier_company_driver = 'DRIVER_CARRIER_COMPANY_USER_TYPE' as const;
/** @deprecated legacy `User.type` constant kept for schema literals. */
export const carrier_company_admin = 'CARRIER_ADMIN_USER_TYPE' as const;

/** @deprecated legacy `User.type` constant kept for schema literals. */
//* customer types
export const business_customer = 'BUSINESS_ADMIN_USER_TYPE' as const;
/** @deprecated legacy `User.type` constant kept for schema literals. */
export const individual_customer = 'CUSTOMER_INDIVIDUAL_USER_TYPE' as const;

/** @deprecated legacy `User.type` constant kept for schema literals. */
//* broker types
export const broker_admin = 'BROKER_ADMIN_USER_TYPE' as const;

/**
 * Legacy enum union of every `User.type` label emitted by the old
 * carrier/customer signup schemas. Kept for the deprecation window
 * — new code discriminates on `PartyRole[]`.
 */
export type LegacyUserTypeString =
  | typeof freelancer_driver
  | typeof carrier_company_driver
  | typeof carrier_company_admin
  | typeof business_customer
  | typeof individual_customer
  | typeof broker_admin;

type TokenData = {
  access_token: string;
  refresh_token: string;
};

export type SignInData = TokenData & {
  user: User | undefined;
  isNewUser: boolean;
};

export type ChangeUserLangData = TokenData & {
  user: User;
};

/** @deprecated split-endpoint response shape. New code uses {@link SignUpData}. */
export type CustomerSignupData = Pick<SignInData, 'access_token' | 'refresh_token'> & {
  business: Org | undefined;
  user: User;
};

export type SendOtpData = {
  /**
   * the id of the otp. THIS IS VERY IMPORTANT when you want to verify the otp
   */
  otpId: string;
  /**
   * this will tell if the number is already registered or not.
   */
  isNewUser: boolean;
  phone: string;
  /**
   * the date at which the OTP will expire
   */
  expiredAt: Date;
};

/** @deprecated split-endpoint response shape. New code uses {@link SignUpData}. */
export type CarrierSignupData = Pick<SignInData, 'access_token' | 'refresh_token'> & {
  carrier: Org | undefined;
  user: User;
  driver: Driver | undefined;
};

/**
 * Unified signup response shape (returned by `POST /auth/sign-up`).
 *
 * Replaces the role-split `CustomerSignupData` / `CarrierSignupData`. The
 * legacy `business` / `carrier` keys are gone — callers read the unified
 * `org` instead. `driver` is present iff the wizard outcome was a driver
 * variant.
 *
 * See `docs/relations-v2/08-user-identity.md` and the unified signup
 * wizard documented in `apps/backend/src/app/auth/README.md`.
 */
export type SignUpData = TokenData & {
  user: User;
  /**
   * the unified Org doc that was created during signup, if the wizard
   * outcome was `personType: 'company'` or `personType: 'driver'` with
   * `driverEmployment: 'company'`.
   */
  org: Org | undefined;
  /**
   * the Driver doc that was created during signup, if the wizard
   * outcome was `personType: 'driver'`.
   */
  driver: Driver | undefined;
};

/**
 * Response of `GET /auth/me` (auth-v2 unified shape).
 *
 * Mirrors {@link SignUpData}: one `org` slot for the user's currently
 * active org (the doc matching `user.currentSignedInOrgId`). The legacy
 * role-split `business` / `carrier` keys are gone — both held the same
 * unified `Org` schema and only one was ever populated. Role-specific
 * handling reads `org.roles` (`PartyRole[]`).
 *
 * The derived `userType` / `isUserCustomer` labels are also gone; derive
 * classification from `user.roles[]` via the `@hero/core` role helpers.
 */
export type AuthGetMeData = {
  user: User;
  /** the unified Org doc for the user's currently active org, if any. */
  org?: Org;
  /** the driver info when the current user is a driver (carrier company driver or freelancer driver) */
  driver?: Driver;
};

export type CanUseAppData = {
  status: 'active' | 'blocked_by_user' | 'blocked_by_org' | 'blocked_by_billing';
  message: string;
  /** Active org display name — populated for `blocked_by_org`. */
  orgName?: string;
};
